import React from 'react';
import { Trophy, Target, Users, Award } from 'lucide-react';

const categories = [
  {
    title: "Professional Teams",
    examples: ["City Titans", "Metro Knights", "State Warriors"],
    description: "Names that convey professionalism, strength, and excellence."
  },
  {
    title: "Local/Amateur Teams",
    examples: ["Community Kings", "District Dragons", "Regional Rebels"],
    description: "Names that build local pride and team spirit."
  },
  {
    title: "Youth Teams",
    examples: ["Junior Jets", "Young Lions", "Future Stars"],
    description: "Names that inspire and encourage young athletes."
  }
];

const tips = [
  {
    icon: Trophy,
    title: "Consider Your Level",
    content: "Match your name to your competitive level and aspirations."
  },
  {
    icon: Target,
    title: "Local Connection",
    content: "Include references to your community or region when appropriate."
  },
  {
    icon: Users,
    title: "Team Unity",
    content: "Choose names that bring your team together and build spirit."
  },
  {
    icon: Award,
    title: "Future Growth",
    content: "Select a name that can grow with your team's success."
  }
];

export default function SportsTeamGuide() {
  return (
    <section className="py-20 bg-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Complete Guide to Sports Team Names
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {categories.map((category, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md transform transition-all duration-300 hover:scale-105">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.title}</h3>
              <ul className="space-y-2 mb-4">
                {category.examples.map((example, i) => (
                  <li key={i} className="text-indigo-600">{example}</li>
                ))}
              </ul>
              <p className="text-gray-600">{category.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Tips for Choosing Your Team Name
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-4">
                  <tip.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{tip.title}</h4>
                <p className="text-gray-600">{tip.content}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-indigo-100 p-8 rounded-xl">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            Advanced Naming Strategies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-indigo-800 mb-4">Name Elements to Consider:</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Sport-specific terminology</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Geographic location</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Team values and culture</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Local history or landmarks</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-indigo-800 mb-4">Common Naming Patterns:</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Location + Mascot (City Lions)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Adjective + Noun (Mighty Warriors)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Color + Mascot (Red Dragons)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span>Natural Elements (Thunder, Lightning)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}